import { useEffect, useContext } from 'react';
import { trackClientPageview } from './client-event-tracking';
import {
  trackSnapchatPageView,
  trackSnapchatShoppingView,
} from './snapchat/client';
import { trackBeaconsPageview } from './beacons/client';
import { trackComscorePageview } from './comscore/client';
import { trackFacebookPageView } from './facebook/client';
import { trackGooglePageview } from './google/client';
import { trackNielsenPageView } from './nielsen/client';
import { trackOutbrainPageView } from './outbrain/client';
import { trackPermutivePageView } from './permutive/client';
import { trackTiktokPageView } from './tiktok/client';
import { trackYahooPageview } from './yahoo/client';
import { getReferrer } from '@buzzfeed/bf-utils/lib/referrer';
import { localization } from '../../utils/localization';
import ABeagleContext from '../../contexts/abeagle';
import DestinationContext from '../../contexts/destination';
import ConsentContext from '../../contexts/consent';
import { useDOMContentLoaded } from '../../hooks/usePageReadyState';
import { wunderkindScript } from './wunderkind/client';

export function usePageviewTracking(buzz) {
  const { getFeatureFlagValue } = useContext(
    ABeagleContext
  );
  const { destination, gtm_enabled } = useContext(DestinationContext);
  const isPermutiveEnabled = getFeatureFlagValue('ADSGROUP-442-permutive');
  const {tracking: { consentValue, isConsentKnown, isConsentReady }} = useContext(ConsentContext);
  const isPageReady = useDOMContentLoaded();

  useEffect(() => {
    if (isConsentReady && consentValue && isPageReady) {
      const allSections = buzz.all_classifications ? buzz.all_classifications.sections : [];
      if (allSections.includes('Books') || allSections.includes('TVAndMovies') || allSections.includes('Celebrity') || buzz.isQuiz) {
        wunderkindScript(buzz);
      }
    }
  }, [consentValue, isConsentReady, isPageReady]);

  useEffect(() => {
    trackClientPageview(buzz);

    if (!gtm_enabled) {
      trackGooglePageview(destination, buzz);
    }
    // Facebook will check consent independently
    trackFacebookPageView(destination, buzz, {
      section: buzz.classification.section,
      Edition: buzz.classification.edition,
      username: buzz.bylines[0].username,
      tags: buzz.tags,
      SocialReferral: getReferrer(),
    });

    trackTiktokPageView();
  }, [buzz, destination]);

  useEffect(() => {
    if (isPermutiveEnabled) {
      // Permutive will check consent independently
      trackPermutivePageView(buzz);
    }
  }, [buzz, isPermutiveEnabled]);

  useEffect(() => {
    if (isConsentReady && consentValue) {
      trackBeaconsPageview(buzz);
      trackSnapchatPageView();
      trackOutbrainPageView();
      if (buzz.classification.edition === 'Japan') {
        trackYahooPageview();
      }
      if (buzz.isShopping) {
        trackSnapchatShoppingView();
      }
      trackNielsenPageView(destination, {
        type: 'static',
        assetid: parseInt(
          Date.now() + Math.random() * 10000000000,
          10
        ).toString(16),
        section: buzz.category.toLowerCase(),
        segA: buzz.isAd ? 'sponsored' : 'editorial',
        segB: 'bzfd',
        segC: localization(buzz).country,
      });
    }
  }, [buzz, consentValue, destination, isConsentReady]);

  useEffect(() => {
    /**
     * Direction regarding consent from ComScore:
     * 1) If consent is unknown at page load, `cs_ucfr`` value should be blank/empty or omitted.
     * 2) After a user selects/accept/reject the consent, no subsequent tag should fire on that
     *    page.
     * 3) After the consent is provided and if the user goes into a sub-section, or another page of
     *    the site, at that time consent is known and the cs_ucfr parameters for that continued
     *    usage of the site should be either be cs_ucfr=1 or 0 based on the consent selected.
     */
    if (isConsentKnown) {
      if (isConsentReady) {
        /**
         * `consentValue` returns a boolean but Comscore requires a number `0` or `1`.
         * `Number(false)` will return 0, `Number(true)` will return 1. The returned value is
         * wrapped in a parseFloat to catch any edge cases, although unlikley, where the returned
         * value is instead a string.
         */
        trackComscorePageview({ cs_ucfr: parseFloat(Number(consentValue)) });
      }
    } else {
      trackComscorePageview();
    }

    /**
     * `isConsentKnown` is not included in the dependency array in the event comscore could then be
     * invoked twice. Once before consent is loaded and again after consent values are set. The
     * directions (1 - 3) in the comment above came directly from Comscore on how to correctly track
     * their pageview, and omitting this property from the dependency array allows for the behavior
     * they describe.
     *
     * `buzz` is not used in this `useEffect` but it's needed in order to trigger a new comscore
     * pageview on route change.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buzz, isConsentReady, consentValue]);
}
